import { Icon } from "@pepperstone/pepper-ui-kit";

import { Client } from "../../api/generated";
import { ResponsiveInfoTooltip } from "../../ResponsiveInfoTooltip";
import { Typo } from "../../Typo";
import {
  CurrencyCodeEnum,
  formatCurrencyWithSymbol,
} from "../../utils/currency";
import { useDebounceFn } from "../../utils/debounce";
import { useOverviewAnalytics } from "../OverviewAnalyticsProvider";
import { OverviewError } from "../types";

export type TopEarnersTableForOverviewProps = {
  earners: Client[] | [];
  translatedContent: TopEarnersTableTranslatedContent;
  redactedFields?: string[];
  isMobile?: boolean;
  isBackoffice: boolean;
  error?: string;
  scaId?: string;
};

export type TopEarnersTableTranslatedContent = {
  viewMore: string;
  header: string;
  clientIdColumn: string;
  nameColumn: string;
  earningsColumn: string;
  noDataFound: string;
  currencyError: string;
  headerDisclaimerTooltip: string;
};

type ColumnType = {
  field: keyof Client;
  isRedacted?: boolean;
  title: string;
};

export const TopEarnersTable = ({
  earners,
  translatedContent,
  redactedFields = [],
  isMobile = false,
  isBackoffice = false,
  error,
  scaId,
}: TopEarnersTableForOverviewProps) => {
  const showButton = earners.length > 0;
  const { analytics } = useOverviewAnalytics();

  const columns: ColumnType[] = [
    {
      field: "clientId",
      isRedacted: redactedFields.includes("clientId"),
      title:
        translatedContent.clientIdColumn +
        (redactedFields.includes("clientId") ? " (HIDDEN)" : ""),
    },
    {
      field: "fullName",
      isRedacted: redactedFields.includes("name"),
      title:
        translatedContent.nameColumn +
        (redactedFields.includes("name") ? " (HIDDEN)" : ""),
    },
  ];

  const onHoverHandler = (isMobile: boolean) => {
    console.log("hover handler?");
    analytics?.trackOverviewTooltipEvent(isMobile);
  };
  const [debouncedOnHoverHandler] = useDebounceFn(onHoverHandler, 1000);

  return (
    <div className="flex flex-1 flex-col overflow-hidden rounded-2xl border border-stroke-primary bg-surface-base-background shadow-sm">
      <div className="flex items-center justify-between p-6">
        <span className="flex flex-row items-center gap-2">
          <Typo
            element="h1"
            type={isMobile ? "labelMD" : "labelLG"}
            textColor="text-text-primary"
          >
            {translatedContent.header}
          </Typo>
          <ResponsiveInfoTooltip
            isMobile={isMobile}
            title={translatedContent.header}
            tooltipText={translatedContent.headerDisclaimerTooltip}
            onHover={debouncedOnHoverHandler}
          />
        </span>

        {showButton ? (
          <button className="font-semibold">
            <a
              href={
                isBackoffice && scaId
                  ? `/clients/${scaId}`
                  : "/clients#trading-report"
              }
              onClick={analytics?.trackGoToTopEarnersEvent}
            >
              <Typo
                element="span"
                type="labelMD"
                smType="labelXS"
                textColor="text-text-primary"
              >
                {translatedContent.viewMore}
              </Typo>
            </a>
          </button>
        ) : null}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-stroke-primary text-sm text-text-secondary">
              {isBackoffice ? (
                columns.map((column) => (
                  <Typo
                    key={column.field as string}
                    element="th"
                    type="captionSM"
                    textColor="text-text-secondary"
                    classes={`pl-6 py-4 text-left ${column.isRedacted ? "bg-red-100" : ""}`}
                  >
                    {column.title}
                  </Typo>
                ))
              ) : (
                <Typo
                  element="th"
                  type="captionSM"
                  textColor="text-text-secondary"
                  classes="pl-6 py-4 text-left"
                >
                  {!redactedFields?.includes("name")
                    ? translatedContent.nameColumn
                    : translatedContent.clientIdColumn}{" "}
                </Typo>
              )}
              <Typo
                element="th"
                type="captionSM"
                textColor="text-text-secondary"
                classes="text-right pr-6 py-4"
              >
                {translatedContent.earningsColumn}
              </Typo>
            </tr>
          </thead>
          <tbody>
            <TopEarnersBody
              earners={earners}
              translatedContent={translatedContent}
              redactedFields={redactedFields}
              isBackoffice={isBackoffice}
              error={error}
              isMobile={isMobile}
              columns={columns}
            />
          </tbody>
        </table>
      </div>
    </div>
  );
};

const TopEarnersBody = ({
  earners,
  translatedContent,
  redactedFields,
  isBackoffice,
  error,
  isMobile,
  columns,
}: {
  earners: Client[] | [];
  isBackoffice: boolean;
  translatedContent: TopEarnersTableTranslatedContent;
  redactedFields: string[];
  error?: string;
  isMobile: boolean;
  columns: ColumnType[];
}) => {
  if (error?.includes(OverviewError.ConvertRateError)) {
    return <TopEarnersBodyError message={translatedContent.currencyError} />;
  }

  if (earners.length === 0) {
    return <TopEarnersBodyNoData message={translatedContent.noDataFound} />;
  }

  return (
    <>
      {earners.map((earner, index) => (
        <tr
          key={index}
          className={`border-stroke-primary transition-colors hover:bg-gray-50 ${index === earners.length - 1 ? "" : "border-b"}`}
        >
          {isBackoffice ? (
            columns.map((column) => (
              <Typo
                key={column.field as string}
                element="td"
                type={isMobile ? "bodySM" : "captionMD"}
                textColor="text-text-primary"
                classes={`pl-6 py-5 text-left ${column.isRedacted ? "bg-red-100" : ""}`}
              >
                {earner[column.field] ?? ""}
              </Typo>
            ))
          ) : (
            <Typo
              element="td"
              type={isMobile ? "bodySM" : "captionMD"}
              textColor="text-text-primary"
              classes="pl-6 py-5 text-left truncate ellipsis"
            >
              {!redactedFields?.includes("name")
                ? earner.fullName
                : earner.clientId}
            </Typo>
          )}
          <Typo
            element="td"
            type={isMobile ? "bodySM" : "captionMD"}
            textColor="text-text-primary"
            classes="text-right pr-6 py-5"
          >
            {error
              ? `... ${earner.currency || CurrencyCodeEnum.USD}`
              : formatCurrencyWithSymbol(
                  earner.totalEarnings,
                  earner.currency || CurrencyCodeEnum.USD,
                  "en-US",
                  true,
                  false,
                )}
          </Typo>
        </tr>
      ))}
    </>
  );
};

const TopEarnersBodyError = ({ message }: { message: string }) => {
  return (
    <tr>
      <td className="px-6 py-6 text-center text-gray-500" colSpan={3}>
        <div className="flex flex-col items-center gap-4">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-amber-600">
            <Icon icon="alert-triangle" size={32} color="white" />
          </div>
          <Typo
            element="p"
            type="bodyMD"
            textColor="text-text-secondary"
            classes="text-center md:whitespace-pre-line"
          >
            {message}
          </Typo>
        </div>
      </td>
    </tr>
  );
};

const TopEarnersBodyNoData = ({ message }: { message: string }) => {
  return (
    <tr>
      <td className="px-6 py-6 text-center text-gray-500" colSpan={3}>
        {message}
      </td>
    </tr>
  );
};
